import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { StateDefinitionCacheService } from 'src/core/services/state-definition/state-definition-cache.service';
import { StateDefinitionDto } from 'src/core/models/state-definition/state-definition.dto';
import {
  isAutoState,
  getStatusLocalizationKey,
  resolveStatusTone,
  getStatusDotClass,
  compareStatusDisplayOrder,
} from 'src/core/helpers/agent-status.helper';
import { StatusItem } from '../../models/status-item.model';

@Component({
  selector: 'app-agent-status-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, LocalizationModule],
  templateUrl: './agent-status-dropdown.component.html',
  styleUrls: ['./agent-status-dropdown.component.scss'],
})
export class AgentStatusDropdownComponent {
  @Input() currentStateId: number | null = null;
  @Input() queuedNextStateId: number | null = null;
  @Input() isOnCallOrAcw = false;

  @Output() statusSelected = new EventEmitter<number>();
  @Output() queuedStatusCleared = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private readonly stateCache = inject(StateDefinitionCacheService);
  private readonly stateDefinitions = toSignal(this.stateCache.getDefinitions(), {
    initialValue: [] as StateDefinitionDto[],
  });

  readonly statusItems = computed(() => this.buildStatusItems(this.stateDefinitions()));

  onItemClick(item: StatusItem): void {
    if (item.isAuto) return;

    if (this.isOnCallOrAcw) {
      if (item.id === this.queuedNextStateId) {
        this.queuedStatusCleared.emit();
      } else {
        this.statusSelected.emit(item.id);
      }
      return;
    }

    if (item.id !== this.currentStateId) {
      this.statusSelected.emit(item.id);
    }

    this.closed.emit();
  }

  private buildStatusItems(definitions: StateDefinitionDto[]): StatusItem[] {
    return definitions
      .filter(d => d.isActive)
      .sort(compareStatusDisplayOrder)
      .map(d => {
        const tone = resolveStatusTone(d.name, d.typeId);

        return {
          id: d.id,
          labelKey: getStatusLocalizationKey(d.name),
          displayName: d.isPredefined ? null : d.name,
          typeId: d.typeId,
          isAuto: isAutoState(d.name),
          dotClass: getStatusDotClass(tone),
        };
      });
  }
}
